import React, { useRef, useState } from 'react';
import { Button, Card, HTMLEditor, Input } from 'src/shared/ui';
import { Filters } from 'src/components/charts/components';
import { TFilters } from 'src/components/charts/components/types';
import { DATE_RANGES } from 'src/views/charts/constants/timeRanges';
import { Box, Divider, Grid, Link, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { HTMLEditorRef } from 'src/shared/ui/HTMLEditor/types';
import { formatInformation } from 'src/components/charts/utils';
import useAuth from 'src/hooks/useAuth';
import { Upload, Users } from 'react-feather';
import useMail from 'src/hooks/useMail';
import { Dropzone } from 'src/components/Dropzone';
import * as XLSX from 'xlsx';
import { isEmail } from 'src/utils/validations';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import {
    CREATE_PERMISSION,
    EMAIL_MARKETING_AUTH,
} from 'src/constants/permissions';

export const MailingEditor = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const editorRef = useRef<HTMLEditorRef>(null);
    const [hasContent, setHasContent] = useState(false);
    const [uploadedEmails, setUploadedEmails] = useState<string[]>([]);
    const { countCampaignContacts, sendCampaign } = useMail();
    const [subject, setSubject] = useState('');
    const [contactsCount, setContactsCount] = useState({
        filters: 0,
        uploaded: 0,
    });

    const [currents, setCurrents] = useState<TFilters>({
        carType: [],
        type: [],
        store: [],
        source: [],
        agent: [],
        from: [],
        createdAt: 'month',
        startDate: DATE_RANGES['month'].gte as string,
        endDate: DATE_RANGES['month'].lte as string,
        isLoaded: 'false',
    });

    const handleChangeSubject = (e: React.ChangeEvent<HTMLInputElement>) =>
        setSubject(e.target.value);

    const handleSendWithFilters = async () => {
        const hasContent = editorRef.current?.hasContent();

        if (!hasContent) return;

        const fullHtml = editorRef.current?.getFullHtml();

        const query = formatInformation(currents, user);

        const response = await sendCampaign({
            subject,
            html: fullHtml,
            filters: query,
            storeId: query.store?.[0],
        });

        if (response)
            setContactsCount((prev) => ({
                ...prev,
                filters: 0,
            }));
    };

    const handleCountContacts = async () => {
        const query = formatInformation(currents, user);

        const count = await countCampaignContacts(query);

        setContactsCount({
            ...contactsCount,
            filters: count,
        });
    };

    const handleAddFile = async (files: File[]) => {
        const file = files[0];
        if (!file) return;

        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);

        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const jsonData: any[] = XLSX.utils.sheet_to_json(sheet);

        const emails = jsonData
            .map((row) => {
                const key = Object.keys(row).find((k) =>
                    k.toLowerCase().includes('email')
                );
                return key ? String(row[key]).trim().toLowerCase() : null;
            })
            .filter((email): email is string => !!email && isEmail(email));

        setUploadedEmails(emails);

        setContactsCount((prev) => ({
            ...prev,
            uploaded: emails.length,
        }));
    };

    const handleSendUploaded = async () => {
        if (!uploadedEmails.length || !hasContent) return;

        const html = editorRef.current?.getFullHtml();

        const query = formatInformation(currents, user);

        const response = await sendCampaign({
            subject,
            html,
            emails: uploadedEmails,
            storeId: query.store?.[0],
        });

        if (response) {
            setUploadedEmails([]);
            setContactsCount((prev) => ({
                ...prev,
                uploaded: 0,
            }));
        }
    };

    const canSendEmailMarketing = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: EMAIL_MARKETING_AUTH,
        crud: CREATE_PERMISSION,
    });

    const isStoreSelected = currents.store && currents.store.length > 0;

    const isInformationComplete = isStoreSelected && hasContent && subject;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
                <Card>
                    <Box display="flex" flexDirection="column" gridGap={20}>
                        <Box display="flex" flexDirection="column" gridGap={10}>
                            <Typography variant="h4">
                                {t('Email.Subject')}
                            </Typography>

                            <Input
                                placeholder={t('Email.Subject')}
                                value={subject}
                                onChange={handleChangeSubject}
                                disabled={!canSendEmailMarketing}
                            />
                        </Box>

                        <Box display="flex" flexDirection="column" gridGap={10}>
                            <Typography variant="h4">
                                {t('Titles.SearchContacts')}
                            </Typography>

                            <Filters
                                currents={currents}
                                setCurrents={setCurrents}
                                loadInformation={handleCountContacts}
                                buttonProps={{
                                    style: {
                                        width: 'calc(50% - 10px)',
                                    },
                                    disabled: !canSendEmailMarketing,
                                }}
                                singleItem={{ store: true }}
                                actionButtons={
                                    contactsCount.filters > 0
                                        ? [
                                              {
                                                  startIcon: <Users />,
                                                  onClick:
                                                      handleSendWithFilters,
                                                  disabled:
                                                      !isInformationComplete,
                                                  label: t(
                                                      'Titles.SendCampaignContacts',
                                                      {
                                                          size: contactsCount.filters,
                                                      }
                                                  ),
                                              },
                                          ]
                                        : undefined
                                }
                            />
                        </Box>

                        <Box display="flex" alignItems="center" gridGap={10}>
                            <Divider style={{ flex: 1 }} />
                            <Typography variant="body2">{t('or')}</Typography>
                            <Divider style={{ flex: 1 }} />
                        </Box>

                        <Box display="flex" flexDirection="column" gridGap={10}>
                            <Typography variant="h4">
                                {t('Titles.UploadContacts')}
                            </Typography>

                            <Dropzone
                                handleAdd={handleAddFile}
                                types="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.csv,text/csv"
                            >
                                <Button
                                    onClick={handleSendUploaded}
                                    fullWidth
                                    startIcon={<Upload />}
                                    disabled={!canSendEmailMarketing}
                                >
                                    {t('Titles.UploadContacts')}
                                </Button>
                            </Dropzone>

                            {contactsCount.uploaded > 0 && (
                                <Button
                                    onClick={handleSendUploaded}
                                    fullWidth
                                    startIcon={<Users />}
                                    disabled={!isInformationComplete}
                                >
                                    {t('Titles.SendCampaignContacts', {
                                        size: contactsCount.uploaded,
                                    })}
                                </Button>
                            )}
                        </Box>

                        <Button
                            intent="secondary"
                            variant="text"
                            onClick={() =>
                                window.open(
                                    '/email-marketing/suscriptions',
                                    '_blank'
                                )
                            }
                        >
                            {t('Plans.Explore')}
                        </Button>
                    </Box>
                </Card>
            </Grid>
            <Grid item xs={12} md={8}>
                <Card>
                    <HTMLEditor
                        ref={editorRef}
                        onChange={() => {
                            const value = editorRef.current?.hasContent();
                            setHasContent(!!value);
                        }}
                    />
                </Card>
            </Grid>
        </Grid>
    );
};
